'use client'

import { useState } from 'react'
import Link from 'next/link'
import { PublicNav } from '@/app/(public)/components/public-nav'
import { PublicFooter } from '@/app/(public)/components/public-footer'
import { Search, BookOpen, Settings, ShoppingBag, Calendar, CreditCard, Users, Globe, ArrowRight, Mail, Phone } from 'lucide-react'
import { HILFE_ARTICLES } from '@/lib/hilfe-articles'

interface Article {
  slug?: string
  category: string
  title: string
  excerpt: string
}

const REAL_ARTICLES: Article[] = HILFE_ARTICLES.map(a => ({
  slug: a.slug,
  category: a.category,
  title: a.title,
  excerpt: a.excerpt,
}))

// Articles still on the roadmap (no body yet) — listed but route to placeholder.
const STUB_ARTICLES: Article[] = [
  { category: 'Erste Schritte', title: 'Logo und Hero-Bild hochladen', excerpt: 'So laden Sie Ihr Logo und ein passendes Hero-Bild für Ihre Restaurant-Website hoch.' },
  { category: 'Reservierungen', title: 'Reservierungen automatisch oder manuell bestätigen', excerpt: 'Stellen Sie ein, wann Reservierungen sofort bestätigt werden und wann manuell.' },
  { category: 'Reservierungen', title: 'Walk-ins direkt erfassen', excerpt: 'Spontan-Gäste in der Tagesübersicht eintragen — ohne Umwege.' },
  { category: 'Bestellungen', title: 'Online-Bestellungen aktivieren (Lieferung & Abholung)', excerpt: 'Schritt für Schritt zur ersten Online-Bestellung.' },
  { category: 'Bestellungen', title: 'Zahlungsmethoden konfigurieren', excerpt: 'Bar, Karte, PayPal — aktivieren Sie nur, was Sie wirklich anbieten möchten.' },
  { category: 'Tischplan', title: 'Räume und Tische einrichten', excerpt: 'Erstellen Sie ein digitales Abbild Ihres Restaurants.' },
  { category: 'Tischplan', title: 'Tische verbinden für Großgruppen', excerpt: 'Mehrere Tische zu einem Gruppen-Tisch zusammenfügen.' },
  { category: 'Gäste', title: 'Gäste-Liste exportieren', excerpt: 'Als CSV für Marketing-Kampagnen oder Buchhaltung.' },
  { category: 'Konto & Abrechnung', title: 'Mitarbeiter-Konten anlegen', excerpt: 'Geben Sie Mitarbeitenden Zugriff mit definierten Rechten.' },
]

const ARTICLES: Article[] = [...REAL_ARTICLES, ...STUB_ARTICLES]

const CATEGORIES = [
  { name: 'Erste Schritte', icon: BookOpen, count: 3 },
  { name: 'Reservierungen', icon: Calendar, count: 3 },
  { name: 'Bestellungen', icon: ShoppingBag, count: 3 },
  { name: 'Speisekarte', icon: BookOpen, count: 3 },
  { name: 'Tischplan', icon: Settings, count: 2 },
  { name: 'Gäste', icon: Users, count: 2 },
  { name: 'Konto & Abrechnung', icon: CreditCard, count: 3 },
  { name: 'Website & SEO', icon: Globe, count: 2 },
]

export default function HilfePage() {
  const [query, setQuery] = useState('')
  const [activeCat, setActiveCat] = useState<string | null>(null)

  const filtered = ARTICLES.filter(a => {
    const matchesQuery = query.trim() === '' || a.title.toLowerCase().includes(query.toLowerCase()) || a.excerpt.toLowerCase().includes(query.toLowerCase())
    const matchesCat = activeCat === null || a.category === activeCat
    return matchesQuery && matchesCat
  })

  return (
    <>
      <PublicNav />
      <main className="bg-pub-bg" style={{ fontFamily: 'var(--font-pub)' }}>
        {/* Hero */}
        <section className="relative overflow-hidden border-b border-pub-rule">
          <div className="absolute inset-0 -z-10 opacity-40"
            style={{ background: 'radial-gradient(60% 60% at 50% 0%, var(--color-pub-accent-bg) 0%, transparent 70%)' }} />
          <div className="mx-auto max-w-3xl px-6 py-20 text-center">
            <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-pub-accent">Hilfe-Center</p>
            <h1 className="text-pub-ink font-semibold leading-[1.05] tracking-tight"
              style={{ fontSize: 'clamp(2.4rem, 4.5vw, 3.6rem)' }}>
              Wie können wir Ihnen helfen?
            </h1>
            <p className="mx-auto mt-6 max-w-xl text-[15px] text-pub-muted">
              Antworten auf häufige Fragen, Schritt-für-Schritt-Anleitungen und Best Practices.
            </p>
            <div className="mt-8 mx-auto max-w-xl relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-pub-muted" />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Was suchen Sie?"
                className="h-13 w-full rounded-full border border-pub-rule bg-pub-surface pl-11 pr-4 text-[14px] text-pub-ink placeholder:text-pub-muted focus:border-pub-accent focus:outline-none focus:ring-2 focus:ring-pub-accent-bg"
              />
            </div>
          </div>
        </section>

        {/* Categories */}
        {!query && (
          <section className="border-b border-pub-rule bg-pub-surface">
            <div className="mx-auto max-w-7xl px-6 py-14">
              <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                {CATEGORIES.map((cat) => {
                  const active = activeCat === cat.name
                  return (
                  <button
                    key={cat.name}
                    onClick={() => setActiveCat(active ? null : cat.name)}
                    className={`group relative overflow-hidden rounded-2xl border p-5 text-left transition-all ${active
                      ? 'border-pub-accent bg-gradient-to-br from-pub-accent-bg to-pub-accent-bg/40 shadow-[0_4px_20px_-4px_var(--color-pub-accent-bg)]'
                      : 'border-pub-rule bg-pub-bg hover:border-pub-accent/60 hover:shadow-md hover:-translate-y-0.5'
                    }`}
                  >
                    <div className={`mb-3.5 inline-flex h-10 w-10 items-center justify-center rounded-xl transition-colors ${active
                      ? 'bg-pub-accent text-white'
                      : 'bg-gradient-to-br from-pub-accent-bg to-pub-accent-bg/30 text-pub-accent group-hover:from-pub-accent group-hover:to-pub-accent group-hover:text-white'
                    }`}>
                      <cat.icon className="h-5 w-5" strokeWidth={1.8} />
                    </div>
                    <p className="text-[14.5px] font-semibold text-pub-ink">{cat.name}</p>
                    <p className="mt-0.5 text-[11.5px] text-pub-muted">{cat.count} Artikel</p>
                  </button>
                  )
                })}
              </div>
            </div>
          </section>
        )}

        {/* Articles */}
        <section className="bg-pub-bg">
          <div className="mx-auto max-w-3xl px-6 py-16">
            <p className="mb-6 text-[12px] font-semibold uppercase tracking-widest text-pub-muted">
              {activeCat ?? (query ? 'Suchergebnisse' : 'Alle Artikel')}
              {' · '}{filtered.length} {filtered.length === 1 ? 'Artikel' : 'Artikel'}
            </p>
            <div className="space-y-3">
              {filtered.length === 0 ? (
                <p className="rounded-xl border border-pub-rule bg-pub-surface p-8 text-center text-[14px] text-pub-muted">
                  Keine Artikel gefunden. Versuchen Sie einen anderen Begriff oder kontaktieren Sie unseren Support.
                </p>
              ) : filtered.map((a, i) => {
                const href = a.slug ? `/hilfe/${a.slug}` : '/service'
                const isStub = !a.slug
                return (
                  <Link key={a.slug ?? `stub-${i}`} href={href}
                    className="group flex items-center justify-between gap-4 rounded-xl border border-pub-rule bg-pub-surface p-5 transition-colors hover:border-pub-accent">
                    <div>
                      <p className="text-[11px] font-semibold uppercase tracking-widest text-pub-accent">
                        {a.category}{isStub && <span className="ml-2 text-pub-muted/60 normal-case">· demnächst</span>}
                      </p>
                      <p className="mt-1 text-[15px] font-semibold text-pub-ink group-hover:text-pub-accent transition-colors">{a.title}</p>
                      <p className="mt-1 text-[13.5px] text-pub-muted">{a.excerpt}</p>
                    </div>
                    <ArrowRight className="h-4 w-4 flex-shrink-0 text-pub-muted group-hover:text-pub-accent transition-colors" />
                  </Link>
                )
              })}
            </div>
          </div>
        </section>

        {/* Contact CTA */}
        <section className="border-t border-pub-rule bg-pub-dark text-white">
          <div className="mx-auto max-w-7xl px-6 py-16 text-center">
            <h2 className="font-semibold tracking-tight" style={{ fontSize: 'clamp(1.6rem, 3vw, 2.2rem)' }}>
              Antwort nicht gefunden?
            </h2>
            <p className="mx-auto mt-3 max-w-lg text-[14px] text-white/60">
              Unser Support-Team antwortet werktags innerhalb weniger Stunden.
            </p>
            <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
              <a href="mailto:support@rezaro.de"
                className="inline-flex items-center gap-2 rounded-full bg-pub-accent px-6 py-3 text-[14px] font-semibold text-white transition-colors hover:bg-pub-accent-dark">
                <Mail className="h-4 w-4" />
                support@rezaro.de
              </a>
              <Link href="/service"
                className="inline-flex items-center gap-2 rounded-full border border-white/20 px-6 py-3 text-[14px] font-semibold text-white hover:bg-white/10 transition-colors">
                <Phone className="h-4 w-4" />
                Service kontaktieren
              </Link>
            </div>
          </div>
        </section>
      </main>
      <PublicFooter />
    </>
  )
}
